import React, { useCallback, useMemo, useRef, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { BsStars } from "react-icons/bs";
import { FiUpload, FiCrop } from "react-icons/fi";
import { uploadFiles } from "../../utils/API_SERVICE_FOR_PATIENT_PAGE";
import { getUserFiles } from "../../utils/API_SERVICE_FOR_PATIENT_PAGE";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { useCrePermission } from "../../Hooks/useCrePermission";
import { CRE_PERMISSION } from "../../cre/crePermissions";
import { sendAgentCommand } from "../../agent/agentConnection";
import { useGlobalStore } from "../../context/UserContext/userContext";
import { COMPANION_APP_LABEL } from "../../agent/constants";
import { parseSnipUploadResponse } from "../../utils/agentResponses";
import { deleteUserFiles } from "../../utils/API_SERVICE_FOR_PATIENT_PAGE";
import { toast } from "react-toastify";
import { FiTrash2, FiLoader, FiFileText } from "react-icons/fi";
import { getScannedDocsSummary } from "../../utils/API_SERVICE_FOR_PATIENT_PAGE";
import DocumentUploadDialog from "./DocumentUploadDialog";
import ImageAnalysisPanel from "./ImageAnalysisPanel";

const DOCUMENT_TYPE = {
  REFERRAL: "referral",
  PRIOR_REPORT: "prior_report",
};

const MAX_FILE_MB = 50;

function AttachedDocument({ studyId, onInsertIntoReport, language = "en" }) {
  const { t } = useTranslation(["common", "acr"]);

  const [files, setFiles] = useState([]);
  const { isCreAuth, isRadicalAuth } = usePatientAuthFlags();
  const { requirePermission } = useCrePermission();
  const showImageAnalysis = !isRadicalAuth;
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [scannedDocsSummary, setScannedDocsSummary] = useState("");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [showTextInput, setShowTextInput] = useState(false);
  const [uploadingText, setUploadingText] = useState(false);
  const [textDocumentType, setTextDocumentType] = useState(
    DOCUMENT_TYPE.REFERRAL,
  );
  const [activeFileCategory, setActiveFileCategory] = useState("all");
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [capturingDocument, setCapturingDocument] = useState(false);
  const filePickerRef = useRef(null);

  // Fetch files for both CRE and Radical users
  React.useEffect(() => {
    async function fetchFiles() {
      setLoadingFiles(true);
      try {
        const fetchedFiles = await getUserFiles();
        setFiles(fetchedFiles);
      } catch (err) {
        setFiles([]);
      } finally {
        setLoadingFiles(false);
      }
    }
    fetchFiles();
  }, []);

  const normalizeDocumentType = useCallback((value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    if (normalized === DOCUMENT_TYPE.REFERRAL) return DOCUMENT_TYPE.REFERRAL;
    if (
      normalized === DOCUMENT_TYPE.PRIOR_REPORT ||
      normalized === "priorreport" ||
      normalized === "prior"
    ) {
      return DOCUMENT_TYPE.PRIOR_REPORT;
    }
    return "other";
  }, []);

  const groupedFiles = useMemo(() => {
    const grouped = {
      all: files || [],
      [DOCUMENT_TYPE.REFERRAL]: [],
      [DOCUMENT_TYPE.PRIOR_REPORT]: [],
    };

    (files || []).forEach((file) => {
      const key = normalizeDocumentType(file.documentType);
      if (grouped[key]) {
        grouped[key].push(file);
      }
    });

    return grouped;
  }, [files, normalizeDocumentType]);

  const visibleFiles = groupedFiles[activeFileCategory] || [];

  const getDocumentTypeLabel = (value) => {
    const raw = String(value || "").trim();
    const normalized = normalizeDocumentType(value);
    if (normalized === DOCUMENT_TYPE.REFERRAL) return t("documentTypeReferral");
    if (normalized === DOCUMENT_TYPE.PRIOR_REPORT)
      return t("documentTypePriorReport");
    // Never render raw tokens/garbage here (some backends return unexpected values).
    // JWTs typically start with "eyJ" and are long base64 segments.
    const looksLikeJwt = raw.startsWith("eyJ") && raw.length > 40;
    if (!raw || looksLikeJwt || raw.length > 40) return t("other") || "Other";
    return raw;
  };

  const handleTextSubmit = async () => {
    if (!textDocumentType) {
      toast.error(t("documentTypeRequired"));
      return;
    }

    if (!textInput.trim()) {
      toast.error(t("pleaseEnterSomeText"));
      return;
    }

    setUploadingText(true);
    try {
      // Convert text to a File/Blob object
      const blob = new Blob([textInput], { type: "text/plain" });
      const fileName = `text-input-${Date.now()}.txt`;
      const textFile = new File([blob], fileName, { type: "text/plain" });

      // Upload using the same uploadFiles function
      const result = await uploadFiles([textFile], textDocumentType);
      if (result.success && result.payload && result.payload.length > 0) {
        const uploaded = result.payload[0];
        // Add to files list
        setFiles((prev) => [
          {
            fileId: uploaded.fileId,
            fileName: uploaded.name || fileName,
            type: "txt",
            url: null,
            uploading: false,
            documentType: uploaded.documentType || textDocumentType,
          },
          ...prev,
        ]);
        toast.success(t("textFileUploadedSuccessfully"));
        setTextInput("");
        setShowTextInput(false);
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      toast.error(t("failedToUploadTextFile"));
      console.error(err);
    } finally {
      setUploadingText(false);
    }
  };

  const handleDocumentUpload = async (file, documentType) => {
    if (!file) return;
    if (!documentType) {
      toast.error(t("documentTypeRequired"));
      return;
    }
    // Optimistically add file to UI
    const tempId = `temp-${Date.now()}`;
    const type =
      file.name
        .split(".")
        ?.pop()
        ?.toLowerCase() || "";
    const url = URL.createObjectURL(file);
    const newFile = {
      fileId: tempId,
      fileName: file.name,
      type,
      url,
      uploading: true,
      documentType,
    };
    setFiles((prev) => [newFile, ...prev]);
    setUploadingDocument(true);

    // Upload logic
    try {
      const result = await uploadFiles([file], documentType);
      if (result.success && result.payload && result.payload.length > 0) {
        const uploaded = result.payload[0];
        // Replace optimistic file with real file info
        setFiles((prev) => [
          {
            fileId: uploaded.fileId,
            fileName: uploaded.name,
            type,
            url: url,
            uploading: false,
            documentType: uploaded.documentType || documentType,
          },
          ...prev.filter((f) => f.fileId !== tempId),
        ]);
        toast.success(t("fileUploadedSuccessfully"));
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      setFiles((prev) => prev.filter((f) => f.fileId !== tempId));
      toast.error(t("failedToUploadFile"));
      console.error(err);
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleScreenCapture = async () => {
    if (!requirePermission(CRE_PERMISSION.SNIPPING_TOOL)) {
      return;
    }
    if (activeFileCategory === "all") {
      toast.error(t("companionAgent.selectDocumentType"));
      return;
    }

    const documentType = activeFileCategory;
    setCapturingDocument(true);
    try {
      const token = await useGlobalStore.getState().getAccessToken();
      const resp = await sendAgentCommand(
        { action: "snip_upload", document_type: documentType },
        token,
      );
      if (!resp) {
        toast.error(
          t("companionAgent.notConnected", { app: COMPANION_APP_LABEL }),
        );
        return;
      }
      const uploaded = parseSnipUploadResponse(resp, documentType);
      const type =
        uploaded.fileName.split(".").pop()?.toLowerCase() || "jpg";

      setFiles((prev) => [
        {
          fileId: uploaded.fileId,
          fileName: uploaded.fileName,
          type,
          url: uploaded.url,
          uploading: false,
          documentType: uploaded.documentType || documentType,
        },
        ...prev,
      ]);
      toast.success(t("companionAgent.captureSuccess"));
    } catch (error) {
      console.error("Attached document snip failed:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("companionAgent.captureFailed"),
      );
    } finally {
      setCapturingDocument(false);
    }
  };

  const onIndexWithAi = async () => {
    if (isCreAuth) {
      // CRE: Get scanned docs summary
      setLoadingSummary(true);
      setScannedDocsSummary("");
      try {
        const res = await getScannedDocsSummary();
        if (res.success && res.payload) {
          // Clean and set the HTML content
          const cleanedHtml = cleanHtmlContent(res.payload);
          setScannedDocsSummary(cleanedHtml);
        } else {
          setScannedDocsSummary("");
        }
      } catch {
        setScannedDocsSummary("");
      } finally {
        setLoadingSummary(false);
      }
    } else {
      // Radical: Get summary
      setLoadingSummary(true);
      setScannedDocsSummary("");
      try {
        const res = await getScannedDocsSummary();
        if (res.success && res.payload) {
          // Clean and set the HTML content
          const cleanedHtml = cleanHtmlContent(res.payload);
          setScannedDocsSummary(cleanedHtml);
        } else {
          setScannedDocsSummary("");
        }
      } catch (err) {
        console.error("Error in summary:", err);
        setScannedDocsSummary("");
      } finally {
        setLoadingSummary(false);
      }
    }
  };

  const handleDeleteFile = async (fileId) => {
    setDeletingFileId(fileId);
    try {
      const response = await deleteUserFiles([fileId]);
      if (response.success) {
        setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
        toast.success(t("fileDeletedSuccessfully"));
      } else {
        toast.error(t("failedToDeleteFile"));
      }
    } catch (err) {
      toast.error(t("errorDeletingFile"));
    } finally {
      setDeletingFileId(null);
    }
  };

  const cleanHtmlContent = (html) => {
    if (!html || typeof html !== "string") return "";
    let cleaned = html;
    // Replace empty <p></p> tags with <br/> for spacing
    cleaned = cleaned.replace(/<p><\/p>/g, "<br/>");
    // Clean up multiple consecutive breaks
    cleaned = cleaned.replace(/(<br\/>\s*){3,}/g, "<br/><br/>");
    // Ensure proper spacing around tags
    cleaned = cleaned.replace(/>\s+</g, "><");
    cleaned = cleaned.replace(/>\s+/g, "> ");
    cleaned = cleaned.replace(/\s+</g, " <");
    return cleaned.trim();
  };

  return (
    <div className="mt-6 dark:bg-gray-900">
      <div className="flex flex-col xl:flex-row xl:h-[80vh]">
        {/* Left: attached documents + AI summary (shared scroll; 50/50 min when empty) */}
        <div
          className={`min-h-0 flex flex-col overflow-hidden bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm dark:shadow-gray-900 rounded-tr-3xl rounded-tl-3xl xl:rounded-bl-3xl ${
            showImageAnalysis
              ? "xl:w-1/2 xl:rounded-tl-3xl xl:rounded-tr-none"
              : "xl:w-full xl:rounded-3xl"
          }`}
        >
          <div className="flex-1 min-h-0 overflow-y-auto">
            <section className="flex-shrink-0 p-4 border-b dark:border-gray-700 xl:min-h-[40vh]">
              <div className="mb-4">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t("attachedDocuments")}
                  </h2>
                  <div className="flex items-center gap-2">
                    {isCreAuth && (
                      <button
                        type="button"
                        onClick={() => void handleScreenCapture()}
                        disabled={uploadingDocument || capturingDocument}
                        className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t("companionAgent.screenCapture")}
                        aria-label={t("companionAgent.screenCapture")}
                      >
                        {capturingDocument ? (
                          <FiLoader className="animate-spin w-3.5 h-3.5 text-gray-900 dark:text-gray-100" />
                        ) : (
                          <FiCrop className="w-3.5 h-3.5 text-gray-900 dark:text-gray-100" />
                        )}
                      </button>
                    )}
                    <button
                      className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setShowTextInput(!showTextInput)}
                      title={t("addTextFile")}
                    >
                      <FiFileText className="text-gray-900 dark:text-gray-100" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Text input field */}
              {showTextInput && (
                <div className="mb-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    {t("addTextFile")}
                  </label>
                  <div className="inline-flex rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 p-1 mb-3 gap-1">
                    <button
                      onClick={() =>
                        setTextDocumentType(DOCUMENT_TYPE.REFERRAL)
                      }
                      className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                        textDocumentType === DOCUMENT_TYPE.REFERRAL
                          ? "bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                          : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      {t("documentTypeReferral")}
                    </button>
                    <button
                      onClick={() =>
                        setTextDocumentType(DOCUMENT_TYPE.PRIOR_REPORT)
                      }
                      className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                        textDocumentType === DOCUMENT_TYPE.PRIOR_REPORT
                          ? "bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                          : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      {t("documentTypePriorReport")}
                    </button>
                  </div>
                  <textarea
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder={t("enterTextHere")}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-transparent transition-all"
                    rows={5}
                    disabled={uploadingText}
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleTextSubmit}
                      disabled={!textInput.trim() || uploadingText}
                      className="px-4 py-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors font-medium"
                    >
                      {uploadingText ? (
                        <>
                          <FiLoader className="animate-spin" />
                          {t("uploading")}
                        </>
                      ) : (
                        <>
                          <FiUpload className="w-4 h-4" />
                          {t("submitText")}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowTextInput(false);
                        setTextInput("");
                      }}
                      disabled={uploadingText}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              )}
              {loadingFiles ? (
                <div className="flex justify-center items-center py-12">
                  <FiLoader className="animate-spin h-10 w-10 text-gray-500" />
                </div>
              ) : (
                <>
                  <div className="mb-3 overflow-x-auto">
                    <div className="inline-flex min-w-full sm:min-w-0 rounded-xl bg-gray-100 dark:bg-gray-700 p-1 gap-1">
                      {[
                        {
                          key: "all",
                          label: t("allDocuments"),
                          count: groupedFiles.all.length,
                        },
                        {
                          key: DOCUMENT_TYPE.REFERRAL,
                          label: t("documentTypeReferral"),
                          count: groupedFiles[DOCUMENT_TYPE.REFERRAL].length,
                        },
                        {
                          key: DOCUMENT_TYPE.PRIOR_REPORT,
                          label: t("documentTypePriorReport"),
                          count:
                            groupedFiles[DOCUMENT_TYPE.PRIOR_REPORT].length,
                        },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => setActiveFileCategory(tab.key)}
                          className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors ${
                            activeFileCategory === tab.key
                              ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow"
                              : "text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-600/60"
                          }`}
                        >
                          {String(tab.label || "").toUpperCase()}
                          {tab.count > 0 && (
                            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#A4C639]/30 dark:bg-[#CBEA7B]/30 text-[10px] font-semibold text-gray-800 dark:text-gray-100">
                              {tab.count}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">
                        <FiUpload />
                        {t("acr:attachmentsSection")}
                      </label>
                      <input
                        ref={filePickerRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          // reset value so selecting the same file again triggers onChange
                          e.target.value = "";

                          // Tab-based upload behavior:
                          // - All: open modal (type selection)
                          // - Otherwise: upload directly to that tab's document type
                          if (activeFileCategory === "all") {
                            setIsUploadDialogOpen(true);
                            return;
                          }
                          handleDocumentUpload(file, activeFileCategory);
                        }}
                        disabled={uploadingDocument || capturingDocument}
                      />
                      <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          if (activeFileCategory === "all") {
                            setIsUploadDialogOpen(true);
                            return;
                          }
                          filePickerRef.current?.click();
                        }}
                        disabled={uploadingDocument || capturingDocument}
                        className="text-xs px-2.5 py-1 rounded-md bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                        title={t("acr:uploadFile")}
                      >
                        {uploadingDocument ? (
                          <FiLoader className="animate-spin w-3.5 h-3.5" />
                        ) : (
                          <FiUpload className="w-3.5 h-3.5" />
                        )}
                        {t("acr:uploadFile")}
                      </button>
                      </div>
                    </div>

                    {visibleFiles && visibleFiles.length > 0 ? (
                      <ul className="space-y-1.5">
                        {visibleFiles.map((file) => (
                          <li
                            key={file.fileId}
                            className="flex items-start justify-between gap-2 px-2 py-1.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                          >
                            <div className="min-w-0">
                              <div className="text-xs text-gray-700 dark:text-gray-200 truncate">
                                {file.fileName}
                              </div>
                              <div className="text-[10px] text-gray-500 dark:text-gray-300 truncate">
                                {getDocumentTypeLabel(file.documentType)}
                              </div>
                            </div>

                            {/* Delete button */}
                            <button
                              className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t("deleteFile")}
                              onClick={() => handleDeleteFile(file.fileId)}
                              disabled={deletingFileId === file.fileId}
                            >
                              {deletingFileId === file.fileId ? (
                                <FiLoader className="animate-spin h-4 w-4 text-red-600" />
                              ) : (
                                <FiTrash2 className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                        {t("acr:noFiles")}
                      </p>
                    )}

                    <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                      {t("acr:acceptedFiles", { max: MAX_FILE_MB })}
                    </p>
                  </div>
                </>
              )}
            </section>

            <section className="flex-shrink-0 flex flex-col p-4 xl:min-h-[30vh]">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <BsStars
                    className="text-[#A4C639] dark:text-[#CBEA7B]"
                    size={20}
                  />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t("aiSummaryTitle")}
                  </h2>
                </div>
                <button
                  className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={onIndexWithAi}
                  title={t("aiProcessing")}
                  disabled={loadingSummary}
                >
                  {loadingSummary ? (
                    <FiLoader className="animate-spin text-gray-900 dark:text-gray-100" />
                  ) : (
                    <BsStars className="text-gray-900 dark:text-gray-100" />
                  )}
                </button>
              </div>
              <div>
                {loadingSummary ? (
                  <div className="flex justify-center items-center py-8">
                    <FiLoader className="animate-spin h-8 w-8 text-gray-500" />
                  </div>
                ) : scannedDocsSummary ? (
                  <div
                    className="prose dark:prose-invert max-w-full prose-sm prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-gray-100"
                    dangerouslySetInnerHTML={{ __html: scannedDocsSummary }}
                  />
                ) : (
                  <p className="text-center py-6 text-sm text-gray-700 dark:text-gray-300">
                    {isCreAuth
                      ? t("noSummaryAvailable")
                      : t("clickAiProcessingToGenerateSummary")}
                  </p>
                )}
              </div>
            </section>
          </div>
        </div>

        {showImageAnalysis && (
          <div className="w-full xl:flex xl:flex-col xl:w-1/2 min-h-0 h-full border dark:border-gray-700 rounded-bl-3xl rounded-br-3xl xl:rounded-bl-none xl:rounded-tr-3xl xl:rounded-br-3xl bg-white dark:bg-gray-800">
            <ImageAnalysisPanel
              studyId={studyId}
              onInsertIntoReport={onInsertIntoReport}
              language={language}
            />
          </div>
        )}
      </div>
      <DocumentUploadDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onUpload={handleDocumentUpload}
        isUploading={uploadingDocument}
        documentTypes={[
          { value: DOCUMENT_TYPE.REFERRAL, label: t("documentTypeReferral") },
          {
            value: DOCUMENT_TYPE.PRIOR_REPORT,
            label: t("documentTypePriorReport"),
          },
        ]}
      />
    </div>
  );
}

export default memo(AttachedDocument);
